import { useCallback, useEffect, useState } from "react";

import { useT } from "../../../shared/i18n";
import { Badge, Card, Skeleton, Table } from "../../../shared/ui";
import { Group } from "../Group";
import { ErrorRetry } from "../ErrorRetry";
import { PermissionDenied } from "../PermissionDenied";
import { isSessionExpired, useReportSessionExpired } from "../sessionContext";
import { formatNumber, formatUsd } from "./format";
import {
  ObservabilityDenied,
  type ErrorsData,
  type ModelsData,
  type ObservabilityApi,
  type RegistryStatus,
} from "./observabilityApi";
import { ProviderSwitches } from "./ProviderSwitches";

const WINDOW_HOURS = 24;

/** Rating is a 0..1 score from the usage ledger; unrated pairs have none. */
function formatRating(value: number | null | undefined): string | null {
  return value === null || value === undefined ? null : value.toFixed(2);
}

function ExposureBadge({ exposure }: { exposure: string | null | undefined }) {
  const t = useT();
  if (exposure === "external") {
    return <Badge tone="warning">{t("admin.models.exposureExternal")}</Badge>;
  }
  if (exposure === "internal") {
    return <Badge tone="success">{t("admin.models.exposureInternal")}</Badge>;
  }
  return <span>-</span>;
}

function BreakerBadge({ state }: { state: string | null | undefined }) {
  const t = useT();
  if (state === "open") {
    return <Badge tone="error">{t("admin.models.breakerOpen")}</Badge>;
  }
  if (state === "half_open") {
    return <Badge tone="info">{t("admin.models.breakerHalfOpen")}</Badge>;
  }
  if (state === "closed") {
    return <Badge tone="success">{t("admin.models.breakerClosed")}</Badge>;
  }
  return <span>-</span>;
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col">
      <span className="truncate text-[12px] leading-4 text-fg-muted">
        {label}
      </span>
      <span className="text-callout font-semibold text-fg">{value}</span>
    </div>
  );
}

/** Mobile rendering of the models table: one card per provider/model. */
function ModelCards({ models }: { models: ModelsData["models"] }) {
  const t = useT();
  if (models.length === 0) {
    return (
      <p className="text-body text-fg-muted sm:hidden">
        {t("admin.models.empty")}
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-2.5 sm:hidden">
      {models.map((row) => (
        <li key={`${row.provider}/${row.model}`}>
          <Card>
            <div className="flex items-center justify-between gap-2">
              <strong className="min-w-0 break-words text-body font-semibold text-fg">
                {`${row.provider} / ${row.model}`}
              </strong>
              <ExposureBadge exposure={row.exposure} />
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-footnote text-fg-muted">
              <span>
                {formatRating(row.rating) ?? t("admin.models.unrated")}
              </span>
              <BreakerBadge state={row.breaker} />
            </div>
            <div className="mt-3 grid grid-cols-4 gap-2">
              <MiniStat
                label={t("admin.models.calls")}
                value={String(row.calls)}
              />
              <MiniStat
                label={t("admin.models.errors")}
                value={String(row.errors)}
              />
              <MiniStat
                label={t("admin.models.mini.p95")}
                value={formatNumber(row.latency_ms_p95, 0)}
              />
              <MiniStat
                label={t("admin.models.mini.usd")}
                value={formatUsd(row.spend_micros)}
              />
            </div>
          </Card>
        </li>
      ))}
    </ul>
  );
}

type Status = "loading" | "ready" | "failed" | "denied";
type RegistryState = "loading" | "ready" | "failed" | "denied";

/**
 * Model usage, spend and error outcomes (OBS-006) plus the provider switches
 * (AGP-015). Without `observability.read` it shows a permission message; the
 * registry and switches show their own when the model registry refuses.
 */
export function ModelsProvidersPanel({ api }: { api: ObservabilityApi }) {
  const t = useT();
  const [models, setModels] = useState<ModelsData | null>(null);
  const [errors, setErrors] = useState<ErrorsData | null>(null);
  const [registry, setRegistry] = useState<RegistryStatus | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const reportExpired = useReportSessionExpired();

  const [registryState, setRegistryState] = useState<RegistryState>("loading");

  const loadRegistry = useCallback(() => {
    api
      .registry()
      .then((next) => {
        setRegistry(next);
        setRegistryState("ready");
      })
      .catch((error: unknown) => {
        if (isSessionExpired(error)) reportExpired();
        if (error instanceof ObservabilityDenied) {
          setRegistry(null);
          setRegistryState("denied");
        } else {
          // A failed refresh keeps the last known registry on screen.
          setRegistryState((current) =>
            current === "ready" ? current : "failed",
          );
        }
      });
  }, [api, reportExpired]);

  const loadUsage = useCallback(() => {
    Promise.all([api.models(WINDOW_HOURS), api.errors(WINDOW_HOURS)])
      .then(([nextModels, nextErrors]) => {
        setModels(nextModels);
        setErrors(nextErrors);
        setStatus("ready");
      })
      .catch((error: unknown) => {
        if (isSessionExpired(error)) reportExpired();
        setStatus(error instanceof ObservabilityDenied ? "denied" : "failed");
      });
  }, [api, reportExpired]);

  useEffect(() => {
    loadUsage();
    loadRegistry();
  }, [loadUsage, loadRegistry]);

  const retry = () => {
    setStatus("loading");
    setRegistryState("loading");
    loadUsage();
    loadRegistry();
  };

  const title = t("admin.models.title");
  return (
    <section aria-label={title} className="flex flex-col gap-3">
      <h2 className="text-title text-fg">{title}</h2>
      {status === "loading" && (
        <div aria-busy="true">
          <Skeleton height={64} />
          <span className="sr-only">{t("admin.load.loading")}</span>
        </div>
      )}
      {status === "denied" && (
        <PermissionDenied message={t("admin.denied.models")} />
      )}
      {status === "failed" && (
        <ErrorRetry message={t("admin.models.failed")} onRetry={retry} />
      )}
      {models !== null && (
        <Group title={t("admin.models.table")}>
          <ModelCards models={models.models} />
          <div className="hidden sm:block">
            <Table
              caption={t("admin.models.table")}
              rows={models.models}
              rowKey={(row) => `${row.provider}/${row.model}`}
              empty={t("admin.models.empty")}
              columns={[
                {
                  id: "model",
                  header: t("admin.models.model"),
                  cell: (row) => `${row.provider} / ${row.model}`,
                },
                {
                  id: "exposure",
                  header: t("admin.models.exposure"),
                  cell: (row) => <ExposureBadge exposure={row.exposure} />,
                },
                {
                  id: "rating",
                  header: t("admin.models.rating"),
                  cell: (row) =>
                    formatRating(row.rating) ?? t("admin.models.unrated"),
                },
                {
                  id: "breaker",
                  header: t("admin.models.breaker"),
                  cell: (row) => <BreakerBadge state={row.breaker} />,
                },
                {
                  id: "calls",
                  header: t("admin.models.calls"),
                  cell: (row) => row.calls,
                },
                {
                  id: "errors",
                  header: t("admin.models.errors"),
                  cell: (row) => row.errors,
                },
                {
                  id: "p50",
                  header: t("admin.models.latencyP50"),
                  cell: (row) => formatNumber(row.latency_ms_p50, 0),
                },
                {
                  id: "p95",
                  header: t("admin.models.latencyP95"),
                  cell: (row) => formatNumber(row.latency_ms_p95, 0),
                },
                {
                  id: "tokens",
                  header: t("admin.models.tokens"),
                  cell: (row) =>
                    `${String(row.input_tokens)}/${String(row.output_tokens)}`,
                },
                {
                  id: "spend",
                  header: t("admin.models.spend"),
                  cell: (row) => formatUsd(row.spend_micros),
                },
              ]}
            />
          </div>
        </Group>
      )}
      {errors !== null && (
        <Group title={t("admin.models.topErrors")}>
          <Table
            caption={t("admin.models.topErrors")}
            rows={errors.top_outcomes}
            rowKey={(row) =>
              `${row.provider}/${row.outcome}/${row.error_class ?? ""}`
            }
            empty={t("admin.models.errorsEmpty")}
            columns={[
              {
                id: "provider",
                header: t("admin.models.provider"),
                cell: (row) => row.provider,
              },
              {
                id: "outcome",
                header: t("admin.models.outcome"),
                cell: (row) => row.outcome,
              },
              {
                id: "class",
                header: t("admin.models.errorClass"),
                cell: (row) => row.error_class ?? "-",
              },
              {
                id: "count",
                header: t("admin.models.count"),
                cell: (row) => row.count,
              },
            ]}
          />
        </Group>
      )}
      {registry !== null && (
        <Group title={t("admin.models.providersTable")}>
          <Table
            caption={t("admin.models.providersTable")}
            rows={registry.providers}
            rowKey={(row) => row.id}
            empty={t("admin.models.empty")}
            columns={[
              {
                id: "provider",
                header: t("admin.models.provider"),
                cell: (row) => row.id,
              },
              {
                id: "exposure",
                header: t("admin.models.exposure"),
                cell: (row) => <ExposureBadge exposure={row.exposure} />,
              },
              {
                id: "rating",
                header: t("admin.models.rating"),
                cell: (row) =>
                  formatRating(row.health?.rating) ?? t("admin.models.unrated"),
              },
              {
                id: "breaker",
                header: t("admin.models.breaker"),
                cell: (row) => <BreakerBadge state={row.health?.breaker} />,
              },
            ]}
          />
        </Group>
      )}
      {registry !== null && (
        <ProviderSwitches
          api={api}
          registry={registry}
          onChanged={loadRegistry}
        />
      )}
      {registryState === "loading" && status !== "denied" && (
        <div aria-busy="true">
          <Skeleton height={48} />
          <span className="sr-only">{t("admin.models.registryLoading")}</span>
        </div>
      )}
      {registryState === "denied" && (
        <PermissionDenied message={t("admin.denied.registry")} />
      )}
      {registryState === "failed" && (
        <ErrorRetry
          message={t("admin.models.registryFailed")}
          onRetry={retry}
        />
      )}
    </section>
  );
}
